import React, { useState, useEffect } from 'react';
import { Wifi, FileText, CreditCard, MessageCircle, Calendar, AlertTriangleIcon } from 'lucide-react';
import { useDarkMode } from '../darkmode/DarkModeContext';
import { useAuth } from '../../pages/auth/AuthContext';
import CustomerChat from '../../pages/chat/CustomerChat';

// API configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

export default function KomponenDashboard({ user: propUser }) {
  const { isDarkMode } = useDarkMode();
  const { user: contextUser, token } = useAuth();
  
  // Prioritize prop user over context user
  const currentUser = propUser || contextUser;
  
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    activePackages: 0,
    orderHistory: 0,
    bills: 0,
    supportTickets: 0,
    latestBill: null,
    activePackageInfo: null,
    loading: true,
    error: null
  });

  // Better token handling
  const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');

  // Get user display name
  const getUserDisplayName = () => {
    if (!currentUser) return 'Pengguna';
    
    if (currentUser.fullName) return currentUser.fullName;
    if (currentUser.name) return currentUser.name;
    if (currentUser.firstName && currentUser.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }
    if (currentUser.firstName) return currentUser.firstName;
    if (currentUser.email) return currentUser.email.split('@')[0];
    return 'Pengguna';
  };

  // Get user contact info for API queries
  const getUserContactInfo = () => {
    if (!currentUser) return {};
    
    return {
      email: currentUser.email,
      phone: currentUser.phone,
      userId: currentUser._id || currentUser.id
    };
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));

      const contactInfo = getUserContactInfo();
      
      // Prepare common query parameters
      const queryParams = new URLSearchParams();
      if (contactInfo.phone) queryParams.append('phone', contactInfo.phone);
      if (contactInfo.email) queryParams.append('email', contactInfo.email);

      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Initialize counters
      let activePackages = 0;
      let orderHistory = 0;
      let bills = 0;
      let supportTickets = 0;
      let latestBill = null;
      let activePackageInfo = null;

      // Fetch orders and packages
      try {
        let ordersResponse;
        
        // Try authenticated endpoint if we have token and user ID
        if (authToken && contactInfo.userId) {
          ordersResponse = await fetch(`${API_BASE_URL}/orders/my-orders`, {
            method: 'GET',
            headers: headers,
            credentials: 'include'
          });
        }

        // Fallback to customer endpoint if authenticated endpoint fails or not available
        if (!ordersResponse || !ordersResponse.ok) {
          ordersResponse = await fetch(`${API_BASE_URL}/orders/customer?${queryParams.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
        }

        if (ordersResponse && ordersResponse.ok) {
          const ordersResult = await ordersResponse.json();
          if (ordersResult.success && ordersResult.data) {
            const orders = Array.isArray(ordersResult.data) ? ordersResult.data : [];
            orderHistory = orders.length;
            
            // Find active packages from orders
            const activeOrders = orders.filter(order => 
              order.status === 'active' || order.status === 'installed' || order.status === 'completed'
            );
            activePackages = activeOrders.length;
            
            // Get the first active package for display
            if (activeOrders.length > 0) {
              const order = activeOrders[0];
              const packageInfo = order.packageSnapshot || order.package || {};
              activePackageInfo = {
                name: packageInfo.name || 'Internet Broadband',
                type: packageInfo.type || 'broadband',
                status: order.status,
                speed: packageInfo.speed || 'Unknown',
                orderNumber: order.orderNumber || `#${order._id?.slice(-4)}`
              };
            }
          }
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      }

      // Fetch bills
      try {
        const billsResponse = await fetch(`${API_BASE_URL}/billings/customer?${queryParams.toString()}`, {
          method: 'GET',
          headers: authToken ? headers : { 'Content-Type': 'application/json' },
          credentials: authToken ? 'include' : 'omit'
        });

        if (billsResponse.ok) {
          const billsResult = await billsResponse.json();
          if (billsResult.success && billsResult.data) {
            const billsData = Array.isArray(billsResult.data) ? billsResult.data : [];
            bills = billsData.length;
            
            // Find the latest unpaid bill
            const unpaidBills = billsData.filter(bill => {
              const paymentStatus = bill.payment?.status || bill.status;
              return paymentStatus === 'pending' || paymentStatus === 'overdue' || paymentStatus === 'unpaid';
            });
            
            if (unpaidBills.length > 0) {
              // Sort by due date and get the latest
              const sortedBills = unpaidBills.sort((a, b) => {
                const dateA = new Date(a.billing?.dueDate || a.dueDate || a.createdAt);
                const dateB = new Date(b.billing?.dueDate || b.dueDate || b.createdAt);
                return dateB - dateA;
              });
              
              const bill = sortedBills[0];
              const packageInfo = bill.packageSnapshot || bill.package || {};
              const pricingInfo = bill.pricing || {};
              
              latestBill = {
                invoiceNumber: bill.invoiceNumber || `#${bill._id?.slice(-4)}`,
                packageName: packageInfo.name || 'Internet Broadband',
                amount: pricingInfo.totalAmount || pricingInfo.amount || bill.amount || 100000,
                dueDate: bill.billing?.dueDate || bill.dueDate || bill.createdAt,
                status: bill.payment?.status || bill.status || 'pending'
              };
            }
          }
        }
      } catch (error) {
        console.error('Error fetching bills:', error);
      }

      // Fetch support tickets (if endpoint exists)
      try {
        if (authToken && contactInfo.userId) {
          const ticketsResponse = await fetch(`${API_BASE_URL}/support/tickets`, {
            method: 'GET',
            headers: headers,
            credentials: 'include'
          });

          if (ticketsResponse.ok) {
            const ticketsResult = await ticketsResponse.json();
            if (ticketsResult.success && ticketsResult.data) {
              supportTickets = Array.isArray(ticketsResult.data) ? ticketsResult.data.length : 0;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching support tickets:', error);
        // Support tickets might not be implemented yet, so don't treat as critical error
      }

      // Update state with fetched data
      setDashboardData({
        activePackages,
        orderHistory,
        bills,
        supportTickets,
        latestBill,
        activePackageInfo,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: 'Gagal memuat data dashboard. Silakan coba lagi.'
      }));
    }
  };

  useEffect(() => {
    // console.log('KomponenDashboard user changed:', currentUser);
    fetchDashboardData();
  }, [currentUser, authToken]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Get package type display
  const getPackageTypeDisplay = (type) => {
    const types = {
      'broadband': 'Internet Broadband',
      'dedicated': 'Dedicated Internet',
      'satelit': 'Internet Satelit',
      'metro-e': 'Metro Ethernet'
    };
    return types[type] || 'Internet Broadband';
  };

  // Dynamic stats based on fetched data
  const stats = [
    { 
      title: 'Paket Saya', 
      value: dashboardData.loading ? '-' : dashboardData.activePackages.toString(), 
      icon: Wifi, 
      bgColor: 'bg-[#1E9B4C]' 
    },
    { 
      title: 'Riwayat Pesanan', 
      value: dashboardData.loading ? '-' : dashboardData.orderHistory.toString(), 
      icon: FileText, 
      bgColor: 'bg-purple-500' 
    },
    { 
      title: 'Tagihan', 
      value: dashboardData.loading ? '-' : dashboardData.bills.toString(), 
      icon: CreditCard, 
      bgColor: 'bg-orange-500' 
    },
    { 
      title: 'Support Tiket', 
      value: dashboardData.loading ? '-' : dashboardData.supportTickets.toString(), 
      icon: MessageCircle, 
      bgColor: 'bg-gray-600' 
    }
  ];

  return (
    <div
      className="py-12 px-6 bg-cover bg-center bg-no-repeat transition-colors duration-300 relative"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      {/* Overlay */}
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-[#dfe4e0ff]/70'}`}></div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8 text-center md:text-left">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Dashboard
          </h1>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
            Selamat datang, {getUserDisplayName()}
          </p>
          {currentUser && currentUser.email && (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {currentUser.email}
            </p>
          )}
        </div>

        {/* Error Display */}
        {dashboardData.error && (
          <div className={`mb-6 p-4 rounded-lg border ${
            isDarkMode 
              ? 'bg-red-900/20 border-red-800 text-red-300' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="flex items-center space-x-2">
              <AlertTriangleIcon className="h-5 w-5" />
              <span>{dashboardData.error}</span>
            </div>
            <button
              onClick={fetchDashboardData}
              className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
            >
              Coba Lagi
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className={`rounded-lg shadow-md border p-6 hover:shadow-lg transition-all
                  ${isDarkMode
                    ? 'bg-gray-800/90 border-gray-700'
                    : 'bg-white/90 backdrop-blur-sm border-gray-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {stat.title}
                    </p>
                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bgColor} p-3 rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Active Internet Package */}
        <div className="mb-8">
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Paket Internet Aktif
          </h2>
          <div
            className={`rounded-lg shadow-md border p-6
              ${isDarkMode
                ? 'bg-gray-800/90 border-gray-700'
                : 'bg-white/90 backdrop-blur-sm border-gray-200'}`}
          >
            {dashboardData.loading ? (
              <div className="animate-pulse">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                  <div className="space-y-2">
                    <div className={`h-4 w-32 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    <div className={`h-3 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                  </div>
                </div>
              </div>
            ) : dashboardData.activePackageInfo ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-[#1E9B4C] p-3 rounded-lg">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {dashboardData.activePackageInfo.name}
                    </h3>
                    <p className={isDarkMode ? 'text-gray-400 text-sm' : 'text-gray-600 text-sm'}>
                      {dashboardData.activePackageInfo.speed} • {getPackageTypeDisplay(dashboardData.activePackageInfo.type)}
                    </p>
                    <p className={isDarkMode ? 'text-gray-500 text-xs' : 'text-gray-500 text-xs'}>
                      {dashboardData.activePackageInfo.orderNumber}
                    </p>
                  </div>
                </div>
                <button className="bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                  {dashboardData.activePackageInfo.status === 'active' ? 'Aktif' : 'Terinstal'}
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className={`mx-auto w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center mb-4`}>
                  <Wifi className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Belum Ada Paket Aktif
                </h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Anda belum memiliki paket internet yang aktif
                </p>
                <button
                  onClick={() => window.location.href = '/produk'}
                  className="bg-[#1E9B4C] hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Lihat Paket
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Latest Bills */}
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Tagihan Terbaru
          </h2>
          <div
            className={`rounded-lg shadow-md border p-6
              ${isDarkMode
                ? 'bg-gray-800/90 border-gray-700'
                : 'bg-white/90 backdrop-blur-sm border-gray-200'}`}
          >
            {dashboardData.loading ? (
              <div className="animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    <div className="space-y-2">
                      <div className={`h-4 w-40 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                      <div className={`h-3 w-32 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    </div>
                  </div>
                  <div className={`h-8 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                </div>
              </div>
            ) : dashboardData.latestBill ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-lg ${
                    dashboardData.latestBill.status === 'overdue' ? 'bg-red-500' : 'bg-orange-500'
                  }`}>
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Tagihan {dashboardData.latestBill.invoiceNumber}
                    </h3>
                    <p className={isDarkMode ? 'text-gray-400 text-sm' : 'text-gray-600 text-sm'}>
                      {dashboardData.latestBill.packageName}
                    </p>
                    <p className={`text-sm ${
                      dashboardData.latestBill.status === 'overdue' ? 'text-red-400' : 'text-orange-400'
                    }`}>
                      Jatuh Tempo: {formatDate(dashboardData.latestBill.dueDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(dashboardData.latestBill.amount)}
                  </p>
                  <button
                    onClick={() => window.location.href = '/billing'}
                    className={`text-sm px-3 py-1 rounded mt-2 font-medium transition-colors ${
                      dashboardData.latestBill.status === 'overdue'
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }`}
                  >
                    {dashboardData.latestBill.status === 'overdue' ? 'Terlambat' : 'Belum Bayar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className={`mx-auto w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center mb-4`}>
                  <CreditCard className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Tidak Ada Tagihan Pending
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Semua tagihan sudah lunas atau belum ada tagihan
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}